/**
 * ============================================================================
 * MERKEZ BAĞLAMA NOKTASI — `repo/createRepository.ts`nin ikizi.
 * ============================================================================
 * Arayüzün geri kalanı yalnızca `CentralPort` / `SyncEngine` tiplerini
 * görür. Merkez istemcisi TEK bir yerden, burada kurulur.
 *
 * TABAN ADRES: varsayılan `/merkez` — yani AYNI KÖKEN. Sebep iki katmanlı:
 *   1. `packages/central` HİÇ CORS başlığı göndermiyor (bilinçli: kimlik
 *      doğrulaması olmayan bir okuma API'si), o yüzden tarayıcıdan doğrudan
 *      `http://localhost:8787`e çağrı yapmak engellenir.
 *   2. Geliştirme sunucusu `Cross-Origin-Embedder-Policy: require-corp` ile
 *      koşuyor (SQLite/OPFS için). Aynı köken bu iki sorunu da yok eder.
 * `vite.config.ts` içindeki vekil `/merkez` → merkez sunucusu eşlemesini
 * yapar. Üretimde aynı yolu statik sunucu vekillemeli.
 *
 * ÇALIŞMA ZAMANI NESNESİ NEREDEN GELİYOR: `@parafon/local`ın derlenmiş
 * tarayıcı çıktısından (`/parafon-db/index.js`) — `createRepository.ts` ile
 * aynı gerekçe (bkz. o dosyanın başındaki not). Böylece mahremiyet denetleyici
 * taşıyıcı (beyaz liste + yasaklı alan taraması) DEVREDE kalır; arayüz asla
 * doğrudan `fetch` çağırmaz.
 */

import type { CentralPort, SyncEngine, SyncReport } from "@parafon/contracts/device-api";
import type { IsoDate } from "@parafon/contracts";
import { createFakeCentral } from "../dev/fakeCentral";
import { createFakeSyncEngine } from "../dev/fakePriceCache";
import { M } from "../dil";

/** Merkez sunucusunun geliştirme vekili — `vite.config.ts` ile eşleşir. */
export const DEFAULT_CENTRAL_BASE_URL = "/merkez";

const CENTRAL_MODULE_URL = "/parafon-db/index.js";

export interface CentralBundle {
  /** null ise merkez YOK; sebebi `unavailable` alanında ve ekranda görünür. */
  readonly central: CentralPort | null;
  readonly sync: SyncEngine | null;
  readonly baseUrl: string | null;
  /** Sahte merkez mi — arayüz bunu kullanıcıya açıkça söyler. */
  readonly isFake: boolean;
  /** Merkez neden kullanılamıyor (Türkçe, kullanıcıya gösterilir). */
  readonly unavailable: string | null;
}

type CentralFactory = (options: { baseUrl: string; timeoutMs?: number; jeton?: () => string | null }) => CentralPort;

// ---------------------------------------------------------------------------
// Gelir modeli (şerit 6): ücretli kasanın oturum jetonu. Kabiliyet sağlayıcısı
// oturum açınca/yenileyince yazar, düşünce/kilitlenince siler. Fiyat ve kasa
// istekleri başlığa koyar; kapı istekleri KOYMAZ.
// ---------------------------------------------------------------------------

let aktifJeton: string | null = null;

export function jetonAyarla(jeton: string | null): void {
  aktifJeton = jeton;
}

export function jetonOku(): string | null {
  return aktifJeton;
}

interface CentralModule {
  readonly createCentralClient: CentralFactory;
  readonly syncPrices: SyncEngine["syncPrices"];
  readonly syncFx: SyncEngine["syncFx"];
}

/**
 * `?merkez=` bayrağı:
 *   `?merkez=kapali` → merkez hiç kurulmaz (uçak modunu elle denemek için)
 *   `?merkez=hata`   → her çağrı düşer (hata yollarını elle denemek için)
 *   `?merkez=<adres>` → taban adres (örn. `?merkez=http://localhost:8787`)
 * Bayrak yoksa `VITE_MERKEZ_URL`, o da yoksa `/merkez`.
 */
function readBaseUrl(params: URLSearchParams): string {
  const flag = params.get("merkez");
  if (flag !== null && flag !== "" && flag !== "kapali" && flag !== "hata") return flag;
  const fromEnv = import.meta.env["VITE_MERKEZ_URL"];
  return typeof fromEnv === "string" && fromEnv !== "" ? fromEnv : DEFAULT_CENTRAL_BASE_URL;
}

export function createCentral(search: string = globalThis.location?.search ?? ""): CentralBundle {
  const params = new URLSearchParams(search);
  const flag = params.get("merkez");

  if (flag === "kapali") {
    return {
      central: null,
      sync: null,
      baseUrl: null,
      isFake: false,
      unavailable: M.kabuk.merkez.kapatildi,
    };
  }

  if (params.has("sahte") || flag === "hata") {
    // Sahte depo ile sahte merkez BİRLİKTE gelir: gerçek depo yoksa yazacak
    // bir önbellek de yoktur.
    const fake =
      flag === "hata"
        ? // dil-disi: geliştirme sahtesinin iç hatası
          createFakeCentral({ failWith: "sahte merkez bilinçli olarak düştü (?merkez=hata)" })
        : createFakeCentral({ latencyMs: 120 });
    return {
      central: fake,
      sync: createFakeSyncEngine(),
      baseUrl: null,
      isFake: true,
      unavailable: null,
    };
  }

  const baseUrl = readBaseUrl(params);
  const pending = loadCentralModule(baseUrl);
  return {
    central: lazyCentral(pending),
    sync: lazySync(pending),
    baseUrl,
    isFake: false,
    unavailable: null,
  };
}

async function loadCentralModule(
  baseUrl: string,
): Promise<{ readonly module: CentralModule; readonly client: CentralPort }> {
  const module = (await import(/* @vite-ignore */ CENTRAL_MODULE_URL)) as Partial<CentralModule>;
  if (typeof module.createCentralClient !== "function") {
    throw new Error(
      "cihaz çekirdeğinin merkez istemcisi bu derlemede yok — `npm run sync:db` çalıştırılmalı",
    );
  }
  return {
    module: module as CentralModule,
    client: module.createCentralClient({ baseUrl, timeoutMs: 30_000, jeton: jetonOku }),
  };
}

/**
 * Söz verilmiş istemciyi eşzamanlı arayüz arkasına saklar (aynı desen depoda
 * da var). Yükleme hatası çağrıya YANSIR — sessizce boş yanıt dönmez.
 */
function lazyCentral(pending: ReturnType<typeof loadCentralModule>): CentralPort {
  const on = <T>(fn: (client: CentralPort) => Promise<T>): Promise<T> =>
    pending.then((loaded) => fn(loaded.client));
  return {
    resolve: (query, options) => on((c) => c.resolve(query, options)),
    track: (instrumentId) => on((c) => c.track(instrumentId)),
    trackStatus: (instrumentId) => on((c) => c.trackStatus(instrumentId)),
    trackedList: () => on((c) => c.trackedList()),
    prices: (ids, from, to) => on((c) => c.prices(ids, from, to)),
    fx: (pairs, from, to) => on((c) => c.fx(pairs, from, to)),
    spot: (ids) => on((c) => c.spot(ids)),
    corporateActions: (ids, from, to) => on((c) => c.corporateActions(ids, from, to)),
  };
}

// ---------------------------------------------------------------------------
// Gelir modeli: fiyat geçmişi derinliği (ücretsiz 1 yıl, geçiş paketi 5 yıl…).
// Kabiliyet sağlayıcısı durum değişince günceller; `null` = sınır yok.
// YALNIZCA FİYAT: kur geçmişi kısılmaz — eski bir alımın maliyeti alım
// gününün kurunu ister ve bugünkü rapor onsuz eksik çıkardı.
// ---------------------------------------------------------------------------

let fiyatGecmisiAltSiniri: IsoDate | null = null;

export function fiyatGecmisiAltSiniriAyarla(gun: IsoDate | null): void {
  fiyatGecmisiAltSiniri = gun;
}

export function fiyatGecmisiAltSiniriOku(): IsoDate | null {
  return fiyatGecmisiAltSiniri;
}

/** İstenen aralığı alt sınıra çeker; tamamı sınırdan eskiyse `null` (istek yapılmaz). */
export function fiyatAraligiKis(from: IsoDate, to: IsoDate, alt: IsoDate | null = fiyatGecmisiAltSiniri): { from: IsoDate; to: IsoDate } | null {
  if (alt === null || from >= alt) return { from, to };
  if (to < alt) return null;
  return { from: alt, to };
}

/** Tamamı sınırdan eski istek: merkeze gidilmez, hiçbir şey değişmez. */
const BOS_FIYAT_SONUCU: SyncReport = {
  upToDate: [],
  permanentlyMissing: [],
  calls: [],
  tracked: [],
  barsWritten: 0,
  rejectedBars: 0,
  changedFrom: null,
  errors: [],
  totalMs: 0,
};

function lazySync(pending: ReturnType<typeof loadCentralModule>): SyncEngine {
  return {
    syncPrices: (options) => {
      const aralik = fiyatAraligiKis(options.from, options.to);
      if (aralik === null) return Promise.resolve(BOS_FIYAT_SONUCU);
      return pending.then((loaded) => loaded.module.syncPrices({ ...options, ...aralik }));
    },
    syncFx: (options) => pending.then((loaded) => loaded.module.syncFx(options)),
  };
}

/**
 * Kasa yedeğinin ("Parafon sunucusu") merkez adresi. Merkez kapalıysa ya da
 * geliştirme sahtesindeyse `null`: bu hedef o oturumda seçilemez (gizlenmez,
 * sebebiyle gösterilir).
 */
export function merkezTabanAdresi(search: string = globalThis.location?.search ?? ""): string | null {
  const params = new URLSearchParams(search);
  const flag = params.get("merkez");
  if (flag === "kapali" || flag === "hata" || params.has("sahte")) return null;
  return readBaseUrl(params);
}

interface TasiyiciModulu {
  readonly createFetchTransport?: (o: { baseUrl: string; timeoutMs?: number; jeton?: () => string | null }) => {
    request(spec: { method: "POST"; path: string; body: Record<string, unknown> }): Promise<unknown>;
  };
}

/** Büyük kasa dosyası yavaş bağlantıda da sığsın. */
const KASA_ZAMAN_ASIMI_MS = 120_000;

/**
 * Kasa isteği cihaz çekirdeğinin taşıyıcısından geçer: beyaz liste + yasaklı
 * alan taraması burada da DEVREDE (CLAUDE.md §6). Modül ilk istekte yüklenir.
 */
export function merkezKasaIstegi(
  baseUrl: string,
): (yol: string, govde: Readonly<Record<string, string>>) => Promise<unknown> {
  let tasiyici: ReturnType<NonNullable<TasiyiciModulu["createFetchTransport"]>> | null = null;
  return async (yol, govde) => {
    if (tasiyici === null) {
      const m = (await import(/* @vite-ignore */ CENTRAL_MODULE_URL)) as TasiyiciModulu;
      if (typeof m.createFetchTransport !== "function") {
        throw new Error("cihaz çekirdeğinin merkez taşıyıcısı bu derlemede yok — sayfayı yenileyin");
      }
      tasiyici = m.createFetchTransport({ baseUrl, timeoutMs: KASA_ZAMAN_ASIMI_MS, jeton: jetonOku });
    }
    return tasiyici.request({ method: "POST", path: yol, body: { ...govde } });
  };
}

/**
 * SERTİFİKA KAPISI'nın taban adresi (gelir modeli): fiyat merkezinden ayrı
 * hizmet, aynı alan adında `/kapi/` (yol isteğin içinde). `?kapi=<adres>`
 * geliştirmede başka adres verir. Merkez kapalıyken ve geliştirme
 * sahtesinde `null`: ücretli kabiliyet sorulmaz, kasa ücretsiz sınırlarla
 * açılır (sebebi kartta söylenir).
 */
export function kapiTabanAdresi(search: string = globalThis.location?.search ?? ""): string | null {
  const params = new URLSearchParams(search);
  const flag = params.get("merkez");
  if (flag === "kapali" || flag === "hata" || params.has("sahte")) return null;
  const kapi = params.get("kapi");
  return kapi !== null && kapi !== "" ? kapi : "";
}

export type KapiIstegi = (yol: string, govde: Readonly<Record<string, unknown>>) => Promise<unknown>;

/** Kapı istekleri de cihaz çekirdeğinin taşıyıcısından geçer (beyaz liste + yasaklı alan). */
export function kapiIstegi(baseUrl: string): KapiIstegi {
  let tasiyici: ReturnType<NonNullable<TasiyiciModulu["createFetchTransport"]>> | null = null;
  return async (yol, govde) => {
    if (tasiyici === null) {
      const m = (await import(/* @vite-ignore */ CENTRAL_MODULE_URL)) as TasiyiciModulu;
      if (typeof m.createFetchTransport !== "function") {
        throw new Error("cihaz çekirdeğinin merkez taşıyıcısı bu derlemede yok — sayfayı yenileyin");
      }
      tasiyici = m.createFetchTransport({ baseUrl, timeoutMs: KAPI_ZAMAN_ASIMI_MS });
    }
    return tasiyici.request({ method: "POST", path: yol, body: { ...govde } });
  };
}

const KAPI_ZAMAN_ASIMI_MS = 15_000;

